import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useHospitalOnboarding } from '../../../contexts/HospitalOnboardingContext';
import {
  Plus,
  Trash2,
  Mic,
  ChevronDown,
  X,
  MapPin,
  Loader2,
  Search,
  Check
} from 'lucide-react';
import { getAmbulanceTypeAbbreviation } from '../../../utils/ambulanceTypeHelper';
import MapPinPicker from '../../../components/ui/MapPinPicker';

export const normalizePlate = (plate) => (plate || '').replace(/[^A-Za-z0-9]/g, '').toUpperCase();
export const isValidPlate = (plate) => {
  const norm = normalizePlate(plate);
  return /^[A-Z]{2}[0-9]{1,2}[A-Z]{0,3}[0-9]{4}$|^[0-9]{2}BH[0-9]{4}[A-Z]{1,2}$/.test(norm);
};

const OWNERSHIP_OPTIONS = ['Owned', 'Leased', 'Attached'];
const INTERCITY_OPTIONS = ['Yes', 'No', 'Emergency Only'];

const DEFAULT_AMBULANCE_TYPES = [
  'ALS',
  'BLS Big',
  'BLS Small',
  'PTV',
  'ACLS',
  'NICU/PICU',
  'Mortuary',
  'Other'
];

const INDIAN_STATES = [
  'Ladakh',
  'Andaman and Nicobar Islands',
  'Andhra Pradesh',
  'Arunachal Pradesh',
  'Assam',
  'Bihar',
  'Chandigarh',
  'Chhattisgarh',
  'Dadra and Nagar Haveli',
  'Daman and Diu',
  'Delhi',
  'Goa',
  'Gujarat',
  'Haryana',
  'Himachal Pradesh',
  'Jammu and Kashmir',
  'Jharkhand',
  'Karnataka',
  'Kerala',
  'Lakshadweep',
  'Madhya Pradesh',
  'Maharashtra',
  'Manipur',
  'Meghalaya',
  'Mizoram',
  'Nagaland',
  'Odisha',
  'Puducherry',
  'Punjab',
  'Rajasthan',
  'Sikkim',
  'Tamil Nadu',
  'Telangana',
  'Tripura',
  'Uttar Pradesh',
  'Uttarakhand',
  'West Bengal'
];

const STATE_CITIES = {
  Ladakh: ['Leh', 'Kargil', 'Nubra', 'Zanskar', 'Drass'],
  Gujarat: ['Ahmedabad', 'Surat', 'Vadodara', 'Rajkot', 'Gandhinagar', 'Bhavnagar', 'Jamnagar'],
  Maharashtra: ['Mumbai', 'Pune', 'Nagpur', 'Thane', 'Nashik', 'Aurangabad', 'Navi Mumbai'],
  Delhi: ['New Delhi', 'North Delhi', 'South Delhi', 'East Delhi', 'West Delhi', 'Central Delhi'],
  Karnataka: ['Bengaluru', 'Mysuru', 'Hubballi', 'Mangaluru', 'Belagavi'],
  Telangana: ['Hyderabad', 'Warangal', 'Nizamabad', 'Karimnagar'],
  'Tamil Nadu': ['Chennai', 'Coimbatore', 'Madurai', 'Tiruchirappalli', 'Salem'],
  'Uttar Pradesh': ['Lucknow', 'Kanpur', 'Varanasi', 'Agra', 'Prayagraj', 'Noida', 'Ghaziabad'],
  Rajasthan: ['Jaipur', 'Jodhpur', 'Udaipur', 'Kota', 'Bikaner'],
  'Madhya Pradesh': ['Bhopal', 'Indore', 'Gwalior', 'Jabalpur', 'Ujjain'],
  Punjab: ['Ludhiana', 'Amritsar', 'Jalandhar', 'Patiala', 'Bathinda'],
  Haryana: ['Gurugram', 'Faridabad', 'Panipat', 'Ambala', 'Karnal'],
  'West Bengal': ['Kolkata', 'Howrah', 'Durgapur', 'Siliguri', 'Asansol'],
  Bihar: ['Patna', 'Gaya', 'Bhagalpur', 'Muzaffarpur'],
  Kerala: ['Thiruvananthapuram', 'Kochi', 'Kozhikode', 'Thrissur']
};

const MEDICAL_EQUIPMENT_OPTIONS = [
  'Defibrillator',
  'Patient Monitor',
  'Ventilator',
  'Suction',
  'Infusion Pump',
  'Syringe Pump',
  'Oxygen',
  'AED',
  'ECG',
  'Stretcher',
  'Wheelchair'
];

const TECHNOLOGY_OPTIONS = [
  'GPS',
  'Dashcam',
  'Cabin Camera',
  'Driver App',
  'IoT Device',
  'Patient Monitor Integration',
  'RISA APS Device'
];

const createEmptyPickupStand = (idNumber) => ({
  id: typeof window !== 'undefined' && window.crypto?.randomUUID
    ? `stand-${idNumber}-${window.crypto.randomUUID()}`
    : `stand-${idNumber}-${Math.random().toString(36).slice(2, 9)}`,
  stand_name: '',
  map_location: '',
  area: '',
  pincode: '',
  state: 'Ladakh',
  city: '',
  service_radius_km: '',
  intercity_available: ''
});

const createEmptyAmbulance = (idNumber) => ({
  id: typeof window !== 'undefined' && window.crypto?.randomUUID
    ? `amb-${idNumber}-${window.crypto.randomUUID()}`
    : `amb-${idNumber}-${Math.random().toString(36).slice(2, 9)}`,
  vehicle_code: '',
  registration_number: '',
  ambulance_type: '',
  ownership: '',
  make_model: '',
  manufacture_year: '',
  pickup_stands: [createEmptyPickupStand(1)],
  medical_equipment: [],
  technology: [],
  documents: {
    rc: '',
    insurance: '',
    fitness_certificate: '',
    permit: '',
    puc: '',
    driver_licence: '',
    ambulance_registration: ''
  },
  parking_location: '',
  service_radius: '',
  operating_status: 'Active & On-Duty'
});

const sanitizeAmbulance = (amb, idx) => {
  const pickupStands = (amb.pickup_stands && amb.pickup_stands.length > 0)
    ? amb.pickup_stands
    : [
        {
          id: `stand-1`,
          stand_name: amb.parking_location || '',
          map_location: amb.lat_long || amb.parking_location || '',
          area: amb.availability_zones?.[0]?.area_pin || '',
          pincode: amb.pincode || '',
          state: amb.state || 'Ladakh',
          city: amb.city || '',
          service_radius_km: amb.vehicle_radius || '',
          intercity_available: amb.intercity_available || ''
        }
      ];

  return {
    ...createEmptyAmbulance(idx + 1),
    ...amb,
    pickup_stands: pickupStands,
    medical_equipment: Array.isArray(amb.medical_equipment) ? amb.medical_equipment : [],
    technology: Array.isArray(amb.technology) ? amb.technology : [],
    documents: {
      rc: amb.documents?.rc || '',
      insurance: amb.documents?.insurance || '',
      fitness_certificate: amb.documents?.fitness_certificate || '',
      permit: amb.documents?.permit || '',
      puc: amb.documents?.puc || '',
      driver_licence: amb.documents?.driver_licence || '',
      ambulance_registration: amb.documents?.ambulance_registration || ''
    }
  };
};

export const Step5AddAmbulances = () => {
  const navigate = useNavigate();
  const {
    formData,
    updateFields,
    saveAndContinue,
    goBack,
    saveAndExit,
    isSaving,
    ambulanceTypeNames
  } = useHospitalOnboarding();

  const availableAmbulanceTypes = React.useMemo(() => {
    const raw =
      ambulanceTypeNames && ambulanceTypeNames.length > 0
        ? ambulanceTypeNames
        : DEFAULT_AMBULANCE_TYPES;
    const seen = new Set();
    const result = [];
    raw.forEach((t) => {
      const abbr = getAmbulanceTypeAbbreviation(t) || t;
      const key = String(abbr).trim().toUpperCase();
      if (!seen.has(key)) {
        seen.add(key);
        result.push({ raw: t, abbr });
      }
    });
    return result;
  }, [ambulanceTypeNames]);

  // Initialize ambulances array
  const [ambulances, setAmbulances] = useState(() => {
    if (formData.ambulances && formData.ambulances.length > 0) {
      return formData.ambulances.map((amb, idx) => sanitizeAmbulance(amb, idx));
    }
    return [createEmptyAmbulance(1)];
  });

  const [activeVehicleIndex, setActiveVehicleIndex] = useState(0);
  const [errors, setErrors] = useState({});

  // Google Map Pin Picker Modal State
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [activeStandIdForMap, setActiveStandIdForMap] = useState(null);

  const currentAmbulance = ambulances[activeVehicleIndex] || ambulances[0];

  // Helper to update current active ambulance top-level fields
  const handleUpdateCurrentVehicle = (field, value) => {
    setAmbulances((prev) => {
      const copy = [...prev];
      copy[activeVehicleIndex] = {
        ...copy[activeVehicleIndex],
        [field]: value
      };
      return copy;
    });

    if (errors[`plate_${activeVehicleIndex}`] || errors.form) {
      setErrors((prev) => {
        const copy = { ...prev };
        delete copy[`plate_${activeVehicleIndex}`];
        delete copy.form;
        return copy;
      });
    }
  };

  // Helper for documents
  const handleUpdateDocument = (docKey, value) => {
    setAmbulances((prev) => {
      const copy = [...prev];
      const prevDocs = copy[activeVehicleIndex].documents || {};
      copy[activeVehicleIndex] = {
        ...copy[activeVehicleIndex],
        documents: {
          ...prevDocs,
          [docKey]: value
        }
      };
      return copy;
    });
  };

  // Add a new ambulance profile
  const handleAddAmbulance = () => {
    const newVehicle = createEmptyAmbulance(ambulances.length + 1);
    setAmbulances((prev) => [...prev, newVehicle]);
    setActiveVehicleIndex(ambulances.length);
  };

  // Delete an ambulance profile
  const handleDeleteAmbulance = (idx, e) => {
    if (e) e.stopPropagation();
    if (ambulances.length <= 1) return;
    const filtered = ambulances.filter((_, i) => i !== idx);
    setAmbulances(filtered);
    setActiveVehicleIndex(Math.max(0, idx - 1));
  };

  // Pickup stand helpers
  const handleAddPickupStand = () => {
    const stands = currentAmbulance.pickup_stands || [];
    const newStand = createEmptyPickupStand(stands.length + 1);
    handleUpdateCurrentVehicle('pickup_stands', [...stands, newStand]);
  };

  const handleUpdatePickupStand = (standId, field, value) => {
    const stands = currentAmbulance.pickup_stands || [];
    const updated = stands.map((st) => {
      if (st.id === standId) {
        const next = { ...st, [field]: value };
        // Reset city if state changes
        if (field === 'state') {
          const cities = STATE_CITIES[value];
          next.city = cities && cities.length > 0 ? cities[0] : '';
        }
        return next;
      }
      return st;
    });
    handleUpdateCurrentVehicle('pickup_stands', updated);
  };

  const handleDeletePickupStand = (standId) => {
    const stands = currentAmbulance.pickup_stands || [];
    if (stands.length <= 1) return;
    const filtered = stands.filter((st) => st.id !== standId);
    handleUpdateCurrentVehicle('pickup_stands', filtered);
  };

  // Google Maps Pin Picker Handlers
  const handleOpenMapModal = (standId) => {
    setActiveStandIdForMap(standId);
    setMapModalOpen(true);
  };

  const handleGoogleMapConfirm = (locationData) => {
    if (!activeStandIdForMap || !locationData) {
      setMapModalOpen(false);
      setActiveStandIdForMap(null);
      return;
    }

    const address = locationData.address || locationData.name || '';

    // Extract 6-digit PIN code from address if present
    const pinMatch = address.match(/\b([1-9][0-9]{5})\b/);
    const extractedPincode = pinMatch ? pinMatch[1] : '';

    // Extract area/locality name
    const addressParts = address.split(',').map((p) => p.trim()).filter(Boolean);
    let extractedArea = '';
    if (locationData.name && locationData.name !== address && !locationData.name.includes(address)) {
      extractedArea = locationData.name;
    } else if (addressParts.length > 2) {
      extractedArea = addressParts.slice(0, 2).join(', ');
    } else if (addressParts.length > 0) {
      extractedArea = addressParts[0];
    }

    // Attempt to match state & city
    let detectedState = '';
    let detectedCity = '';
    for (const stName of INDIAN_STATES) {
      const stateRegex = new RegExp(`\\b${stName}\\b`, 'i');
      if (stateRegex.test(address)) {
        detectedState = stName;
        const cities = STATE_CITIES[stName] || [];
        for (const c of cities) {
          const cityRegex = new RegExp(`\\b${c}\\b`, 'i');
          if (cityRegex.test(address)) {
            detectedCity = c;
            break;
          }
        }
        break;
      }
    }

    const stands = currentAmbulance.pickup_stands || [];
    const updated = stands.map((st) => {
      if (st.id === activeStandIdForMap) {
        return {
          ...st,
          map_location: address,
          area: extractedArea || st.area,
          pincode: extractedPincode || st.pincode,
          state: detectedState || st.state || 'Ladakh',
          city: detectedCity || (detectedState ? (STATE_CITIES[detectedState]?.[0] || st.city) : st.city),
          latitude: locationData.latitude,
          longitude: locationData.longitude
        };
      }
      return st;
    });

    handleUpdateCurrentVehicle('pickup_stands', updated);
    setMapModalOpen(false);
    setActiveStandIdForMap(null);
  };

  // Capabilities toggles
  const handleToggleEquipment = (item) => {
    const current = currentAmbulance.medical_equipment || [];
    const updated = current.includes(item)
      ? current.filter((x) => x !== item)
      : [...current, item];
    handleUpdateCurrentVehicle('medical_equipment', updated);
  };

  const handleToggleTechnology = (item) => {
    const current = currentAmbulance.technology || [];
    const updated = current.includes(item)
      ? current.filter((x) => x !== item)
      : [...current, item];
    handleUpdateCurrentVehicle('technology', updated);
  };

  // Simulated Document file upload handler
  const handleDocFileChange = (docKey, e) => {
    const file = e.target.files?.[0];
    handleUpdateDocument(docKey, file ? file.name : '');
  };

  // Submission handler
  const handleContinue = (e) => {
    e.preventDefault();
    setErrors({});
    const newErrors = {};

    const seenPlates = new Map();
    const seenDLs = new Map();
    const seenMobiles = new Map();

    for (let i = 0; i < ambulances.length; i++) {
      const amb = ambulances[i];
      const rawPlate = (amb.registration_number || '').trim();
      const normPlate = normalizePlate(rawPlate);

      if (rawPlate) {
        if (!isValidPlate(rawPlate)) {
          newErrors[`plate_${i}`] = `Ambulance #${i + 1} plate is invalid (e.g. MH12AB1234 or 22BH1234AA).`;
          newErrors.form = `Ambulance #${i + 1} has an invalid plate format.`;
          setActiveVehicleIndex(i);
          setErrors(newErrors);
          return;
        }

        if (seenPlates.has(normPlate)) {
          const prevIdx = seenPlates.get(normPlate);
          newErrors[`plate_${i}`] = `Duplicate plate number "${rawPlate}". Already used by Ambulance #${prevIdx + 1}.`;
          newErrors.form = `Duplicate vehicle registration number detected: ${rawPlate}.`;
          setActiveVehicleIndex(i);
          setErrors(newErrors);
          return;
        }
        seenPlates.set(normPlate, i);
      }

      // Check primary driver duplicate license across ambulances
      const rawDL = (amb.primary_driver?.driving_licence || '').trim();
      const normDL = rawDL.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
      if (normDL) {
        if (seenDLs.has(normDL)) {
          const prevIdx = seenDLs.get(normDL);
          newErrors.form = `Driver licence (${rawDL}) is entered for both Ambulance #${prevIdx + 1} and Ambulance #${i + 1}. Each ambulance must have a distinct driver.`;
          setActiveVehicleIndex(i);
          setErrors(newErrors);
          return;
        }
        seenDLs.set(normDL, i);
      }

      // Check primary driver duplicate phone number across ambulances
      const rawMobile = (amb.primary_driver?.mobile || '').trim().replace(/\D/g, '');
      if (rawMobile.length === 10) {
        if (seenMobiles.has(rawMobile)) {
          const prevIdx = seenMobiles.get(rawMobile);
          newErrors.form = `Driver phone number (${rawMobile}) is assigned to both Ambulance #${prevIdx + 1} and Ambulance #${i + 1}. Each driver must have a unique phone number.`;
          setActiveVehicleIndex(i);
          setErrors(newErrors);
          return;
        }
        seenMobiles.set(rawMobile, i);
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Prepare ambulances with backward compatible base parking location
    const formattedAmbulances = ambulances.map((amb) => ({
      ...amb,
      parking_location: amb.pickup_stands?.[0]?.stand_name || amb.pickup_stands?.[0]?.map_location || amb.parking_location || '',
      service_radius: amb.pickup_stands?.[0]?.service_radius_km || amb.service_radius || ''
    }));

    updateFields({
      ambulances: formattedAmbulances,
      total_ambulances_enrolled: formattedAmbulances.length
    });

    saveAndContinue();
  };

  return (
    <div className="w-full">
      {/* STEP 5 Header matching Image with Smart Ambulance Blue Theme */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 pb-1">
        <div>
          <span className="text-xs font-bold text-blue-600 tracking-wider uppercase block mb-1">
            STEP 5 OF 6
          </span>
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight leading-tight">
            Add ambulances
          </h2>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">
            Complete each ambulance profile, then add another if needed.
          </p>
        </div>

        <button
          type="button"
          onClick={() => navigate('/hospital/onboarding/voice?type=fleet&step=5')}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-xs font-semibold shadow-2xs transition-colors self-start cursor-pointer"
        >
          <Mic className="w-3.5 h-3.5 text-slate-600" />
          <span>Switch to voice</span>
        </button>
      </div>

      {/* Progress / Accent Bar with Smart Ambulance Blue Theme */}
      <div className="relative w-full h-[2px] bg-slate-200 my-4 rounded-full overflow-hidden">
        <div className="absolute left-0 top-0 h-full w-5/6 bg-blue-600 rounded-full" />
      </div>

      {/* Error Banner */}
      {errors.form && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700 font-medium">
          {errors.form}
        </div>
      )}

      <form onSubmit={handleContinue}>
        {/* Ambulance Selector Card */}
        <div className="bg-white rounded-2xl border border-slate-200/90 shadow-2xs p-4 sm:p-5 mb-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h3 className="text-base font-bold text-slate-900">
              Ambulance {activeVehicleIndex + 1}
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {ambulances.length} vehicle profile(s) in this application
            </p>
          </div>

          <div className="flex items-center gap-2">
            <div className="relative">
              <select
                value={activeVehicleIndex}
                onChange={(e) => setActiveVehicleIndex(Number(e.target.value))}
                className="w-full sm:w-auto py-2 pl-3 pr-8 bg-white border border-slate-300 rounded-lg text-xs font-semibold text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 appearance-none cursor-pointer"
              >
                {ambulances.map((_, idx) => (
                  <option key={idx} value={idx}>
                    Ambulance {idx + 1}
                  </option>
                ))}
              </select>
              <ChevronDown className="w-4 h-4 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>

            {ambulances.length > 1 && (
              <button
                type="button"
                onClick={(e) => handleDeleteAmbulance(activeVehicleIndex, e)}
                className="p-2 text-slate-400 hover:text-red-500 rounded-lg hover:bg-red-50 transition-colors"
                title="Delete this ambulance profile"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* CARD 1: Vehicle profile */}
        <div className="bg-white rounded-2xl border border-slate-200/90 shadow-2xs p-5 sm:p-6 mb-5">
          <h3 className="text-base font-bold text-slate-900 mb-4">
            Vehicle profile
          </h3>

          <div className="overflow-x-auto border border-slate-200/80 rounded-xl bg-slate-50/40">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="border-b border-slate-200 text-slate-700 font-bold whitespace-nowrap bg-slate-50/80">
                  <th className="py-3 px-3 w-10 text-center">#</th>
                  <th className="py-3 px-3 min-w-[170px]">Vehicle Code / Name</th>
                  <th className="py-3 px-3 min-w-[170px]">Registration Number</th>
                  <th className="py-3 px-3 min-w-[170px]">Ambulance Type</th>
                  <th className="py-3 px-3 min-w-[160px]">Ownership</th>
                  <th className="py-3 px-3 min-w-[180px]">Make & Model</th>
                  <th className="py-3 px-3 min-w-[150px]">Manufacturing Year</th>
                </tr>
              </thead>
              <tbody>
                <tr className="bg-white">
                  <td className="py-3 px-3 font-semibold text-slate-500 text-center">
                    {activeVehicleIndex + 1}
                  </td>
                  {/* Vehicle Code / Name */}
                  <td className="py-3 px-3">
                    <input
                      type="text"
                      value={currentAmbulance.vehicle_code || ''}
                      onChange={(e) => handleUpdateCurrentVehicle('vehicle_code', e.target.value)}
                      placeholder="e.g. AMB-01"
                      className="w-full py-2 px-3 bg-white border border-slate-200 rounded-lg text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
                    />
                  </td>
                  {/* Registration Number */}
                  <td className="py-3 px-3">
                    <input
                      type="text"
                      value={currentAmbulance.registration_number || ''}
                      onChange={(e) => handleUpdateCurrentVehicle('registration_number', e.target.value.toUpperCase())}
                      placeholder="DL 01 AB 1234"
                      className={`w-full py-2 px-3 bg-white border rounded-lg text-xs font-mono font-semibold text-slate-800 uppercase focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 ${
                        errors[`plate_${activeVehicleIndex}`] ? 'border-red-400 bg-red-50/30' : 'border-slate-200'
                      }`}
                    />
                    {errors[`plate_${activeVehicleIndex}`] && (
                      <span className="text-[10px] text-red-600 block mt-1">
                        {errors[`plate_${activeVehicleIndex}`]}
                      </span>
                    )}
                  </td>
                  {/* Ambulance Type */}
                  <td className="py-3 px-3">
                    <div className="relative">
                      <select
                        value={currentAmbulance.ambulance_type || ''}
                        onChange={(e) => handleUpdateCurrentVehicle('ambulance_type', e.target.value)}
                        title={currentAmbulance.ambulance_type || 'Select an option'}
                        className="w-full py-2 pl-3 pr-8 bg-white border border-slate-200 rounded-lg text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 appearance-none cursor-pointer"
                      >
                        <option value="">Select an option</option>
                        {availableAmbulanceTypes.map(({ raw, abbr }) => (
                          <option key={raw} value={abbr} title={raw}>
                            {abbr}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                    </div>
                  </td>
                  {/* Ownership */}
                  <td className="py-3 px-3">
                    <div className="relative">
                      <select
                        value={currentAmbulance.ownership || ''}
                        onChange={(e) => handleUpdateCurrentVehicle('ownership', e.target.value)}
                        className="w-full py-2 pl-3 pr-8 bg-white border border-slate-200 rounded-lg text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 appearance-none cursor-pointer"
                      >
                        <option value="">Select an option</option>
                        {OWNERSHIP_OPTIONS.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                    </div>
                  </td>
                  {/* Make & Model */}
                  <td className="py-3 px-3">
                    <input
                      type="text"
                      value={currentAmbulance.make_model || ''}
                      onChange={(e) => handleUpdateCurrentVehicle('make_model', e.target.value)}
                      placeholder="Force Traveller, Tata Winger..."
                      className="w-full py-2 px-3 bg-white border border-slate-200 rounded-lg text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
                    />
                  </td>
                  {/* Manufacturing Year */}
                  <td className="py-3 px-3">
                    <input
                      type="text"
                      value={currentAmbulance.manufacture_year || ''}
                      onChange={(e) => handleUpdateCurrentVehicle('manufacture_year', e.target.value.replace(/\D/g, '').slice(0, 4))}
                      placeholder="2022"
                      className="w-full py-2 px-3 bg-white border border-slate-200 rounded-lg text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* CARD 2: Pickup stands */}
        <div className="bg-white rounded-2xl border border-slate-200/90 shadow-2xs p-5 sm:p-6 mb-5">
          <div className="flex items-center justify-between pb-3 mb-2">
            <h3 className="text-base font-bold text-slate-900">
              Pickup stands
            </h3>
            <button
              type="button"
              onClick={handleAddPickupStand}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:text-blue-700 transition-colors cursor-pointer"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>+ Add row</span>
            </button>
          </div>

          <div className="overflow-x-auto border border-slate-200/80 rounded-xl bg-slate-50/40">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="border-b border-slate-200 text-slate-700 font-bold whitespace-nowrap bg-slate-50/80">
                  <th className="py-3 px-3 w-10 text-center">#</th>
                  <th className="py-3 px-3 min-w-[150px]">Pick Up Stand</th>
                  <th className="py-3 px-3 min-w-[240px]">Map Location</th>
                  <th className="py-3 px-3 min-w-[140px]">Area</th>
                  <th className="py-3 px-3 min-w-[110px]">PIN Code</th>
                  <th className="py-3 px-3 min-w-[140px]">State</th>
                  <th className="py-3 px-3 min-w-[140px]">City</th>
                  <th className="py-3 px-3 min-w-[140px]">Service Radius (KM)</th>
                  <th className="py-3 px-3 min-w-[150px]">Intercity Available</th>
                  <th className="py-3 px-2 w-10 text-center"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {(currentAmbulance.pickup_stands || []).map((stand, sIdx) => {
                  const stateCities = STATE_CITIES[stand.state] || [];
                  return (
                    <tr key={stand.id || sIdx} className="hover:bg-slate-50/60 transition-colors">
                      <td className="py-3 px-3 font-semibold text-slate-500 text-center">
                        {sIdx + 1}
                      </td>
                      {/* Pick Up Stand */}
                      <td className="py-3 px-3">
                        <input
                          type="text"
                          value={stand.stand_name || ''}
                          onChange={(e) => handleUpdatePickupStand(stand.id, 'stand_name', e.target.value)}
                          placeholder="e.g. Main Stand"
                          className="w-full py-2 px-3 bg-white border border-slate-200 rounded-lg text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
                        />
                      </td>
                      {/* Map Location */}
                      <td className="py-3 px-3">
                        <input
                          type="text"
                          value={stand.map_location || ''}
                          onChange={(e) => handleUpdatePickupStand(stand.id, 'map_location', e.target.value)}
                          placeholder="Type place, area or address"
                          className="w-full py-2 px-3 bg-white border border-slate-200 rounded-lg text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
                        />
                        <div className="mt-1 flex items-center justify-between">
                          <button
                            type="button"
                            onClick={() => handleOpenMapModal(stand.id)}
                            className="text-blue-600 hover:text-blue-700 font-semibold text-[11px] inline-flex items-center gap-1 cursor-pointer transition-colors"
                          >
                            <MapPin className="w-3.5 h-3.5 text-blue-600" />
                            <span>+ Select on Google Map</span>
                          </button>
                          <span className="text-[10px] font-medium text-slate-400">Google Maps</span>
                        </div>
                      </td>
                      {/* Area */}
                      <td className="py-3 px-3">
                        <input
                          type="text"
                          value={stand.area || ''}
                          onChange={(e) => handleUpdatePickupStand(stand.id, 'area', e.target.value)}
                          placeholder="Area name"
                          className="w-full py-2 px-3 bg-white border border-slate-200 rounded-lg text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
                        />
                      </td>
                      {/* PIN Code */}
                      <td className="py-3 px-3">
                        <input
                          type="text"
                          value={stand.pincode || ''}
                          onChange={(e) => handleUpdatePickupStand(stand.id, 'pincode', e.target.value.replace(/\D/g, '').slice(0, 6))}
                          placeholder="6 digits"
                          className="w-full py-2 px-3 bg-white border border-slate-200 rounded-lg text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
                        />
                      </td>
                      {/* State */}
                      <td className="py-3 px-3">
                        <div className="relative">
                          <select
                            value={stand.state || 'Ladakh'}
                            onChange={(e) => handleUpdatePickupStand(stand.id, 'state', e.target.value)}
                            className="w-full py-2 pl-3 pr-8 bg-white border border-slate-200 rounded-lg text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 appearance-none cursor-pointer"
                          >
                            <option value="">Select state</option>
                            {INDIAN_STATES.map((st) => (
                              <option key={st} value={st}>
                                {st}
                              </option>
                            ))}
                          </select>
                          <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                        </div>
                      </td>
                      {/* City */}
                      <td className="py-3 px-3">
                        <div className="relative">
                          {stateCities.length > 0 ? (
                            <select
                              value={stand.city || ''}
                              onChange={(e) => handleUpdatePickupStand(stand.id, 'city', e.target.value)}
                              className="w-full py-2 pl-3 pr-8 bg-white border border-slate-200 rounded-lg text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 appearance-none cursor-pointer"
                            >
                              <option value="">Select city</option>
                              {stateCities.map((c) => (
                                <option key={c} value={c}>
                                  {c}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <input
                              type="text"
                              value={stand.city || ''}
                              onChange={(e) => handleUpdatePickupStand(stand.id, 'city', e.target.value)}
                              placeholder="City"
                              className="w-full py-2 px-3 bg-white border border-slate-200 rounded-lg text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
                            />
                          )}
                          {stateCities.length > 0 && (
                            <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                          )}
                        </div>
                      </td>
                      {/* Service Radius (KM) */}
                      <td className="py-3 px-3">
                        <input
                          type="text"
                          value={stand.service_radius_km || ''}
                          onChange={(e) => handleUpdatePickupStand(stand.id, 'service_radius_km', e.target.value.replace(/\D/g, ''))}
                          placeholder="e.g. 25"
                          className="w-full py-2 px-3 bg-white border border-slate-200 rounded-lg text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
                        />
                      </td>
                      {/* Intercity Available */}
                      <td className="py-3 px-3">
                        <div className="relative">
                          <select
                            value={stand.intercity_available || ''}
                            onChange={(e) => handleUpdatePickupStand(stand.id, 'intercity_available', e.target.value)}
                            className="w-full py-2 pl-3 pr-8 bg-white border border-slate-200 rounded-lg text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 appearance-none cursor-pointer"
                          >
                            <option value="">Select an option</option>
                            {INTERCITY_OPTIONS.map((opt) => (
                              <option key={opt} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </select>
                          <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                        </div>
                      </td>
                      {/* Remove Row Button */}
                      <td className="py-3 px-2 text-center">
                        {(currentAmbulance.pickup_stands || []).length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleDeletePickupStand(stand.id)}
                            className="p-1.5 text-slate-400 hover:text-red-500 rounded-md hover:bg-red-50 transition-colors"
                            title="Remove stand"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* CARD 3: Capabilities */}
        <div className="bg-white rounded-2xl border border-slate-200/90 shadow-2xs p-5 sm:p-6 mb-5">
          <h3 className="text-base font-bold text-slate-900 mb-4">
            Capabilities
          </h3>

          {/* Medical Equipment */}
          <div className="mb-5">
            <span className="text-xs sm:text-sm font-semibold text-slate-800 block mb-3">
              Medical Equipment
            </span>
            <div className="flex flex-wrap gap-2.5">
              {MEDICAL_EQUIPMENT_OPTIONS.map((item) => {
                const checked = (currentAmbulance.medical_equipment || []).includes(item);
                return (
                  <label
                    key={item}
                    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-medium cursor-pointer transition-colors select-none ${
                      checked
                        ? 'border-blue-500 bg-blue-50/60 text-blue-900 font-semibold'
                        : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => handleToggleEquipment(item)}
                      className="w-3.5 h-3.5 rounded text-blue-600 focus:ring-blue-500 border-slate-300 cursor-pointer"
                    />
                    <span>{item}</span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Technology */}
          <div>
            <span className="text-xs sm:text-sm font-semibold text-slate-800 block mb-3">
              Technology
            </span>
            <div className="flex flex-wrap gap-2.5">
              {TECHNOLOGY_OPTIONS.map((item) => {
                const checked = (currentAmbulance.technology || []).includes(item);
                return (
                  <label
                    key={item}
                    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-medium cursor-pointer transition-colors select-none ${
                      checked
                        ? 'border-blue-500 bg-blue-50/60 text-blue-900 font-semibold'
                        : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => handleToggleTechnology(item)}
                      className="w-3.5 h-3.5 rounded text-blue-600 focus:ring-blue-500 border-slate-300 cursor-pointer"
                    />
                    <span>{item}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>

        {/* CARD 4: Vehicle documents */}
        <div className="bg-white rounded-2xl border border-slate-200/90 shadow-2xs p-5 sm:p-6 mb-5">
          <h3 className="text-base font-bold text-slate-900 mb-4">
            Vehicle documents
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
            {/* Column 1 */}
            <div className="space-y-4">
              {/* RC */}
              <div>
                <label className="text-xs font-semibold text-slate-800 block mb-1.5">
                  RC
                </label>
                <div className="border border-slate-200 rounded-lg p-1.5 bg-white flex items-center justify-between">
                  <input
                    type="file"
                    onChange={(e) => handleDocFileChange('rc', e)}
                    className="w-full text-xs text-slate-500 file:mr-3 file:py-1 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200 cursor-pointer"
                  />
                  {currentAmbulance.documents?.rc && (
                    <span className="text-[11px] text-blue-600 font-medium truncate max-w-[150px] shrink-0 mr-2">
                      {currentAmbulance.documents.rc}
                    </span>
                  )}
                </div>
                <p className="text-[11px] text-slate-400 mt-1">
                  Preview only; files are not uploaded.
                </p>
              </div>

              {/* Fitness Certificate */}
              <div>
                <label className="text-xs font-semibold text-slate-800 block mb-1.5">
                  Fitness Certificate
                </label>
                <div className="border border-slate-200 rounded-lg p-1.5 bg-white flex items-center justify-between">
                  <input
                    type="file"
                    onChange={(e) => handleDocFileChange('fitness_certificate', e)}
                    className="w-full text-xs text-slate-500 file:mr-3 file:py-1 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200 cursor-pointer"
                  />
                  {currentAmbulance.documents?.fitness_certificate && (
                    <span className="text-[11px] text-blue-600 font-medium truncate max-w-[150px] shrink-0 mr-2">
                      {currentAmbulance.documents.fitness_certificate}
                    </span>
                  )}
                </div>
                <p className="text-[11px] text-slate-400 mt-1">
                  Preview only; files are not uploaded.
                </p>
              </div>

              {/* PUC */}
              <div>
                <label className="text-xs font-semibold text-slate-800 block mb-1.5">
                  PUC
                </label>
                <div className="border border-slate-200 rounded-lg p-1.5 bg-white flex items-center justify-between">
                  <input
                    type="file"
                    onChange={(e) => handleDocFileChange('puc', e)}
                    className="w-full text-xs text-slate-500 file:mr-3 file:py-1 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200 cursor-pointer"
                  />
                  {currentAmbulance.documents?.puc && (
                    <span className="text-[11px] text-blue-600 font-medium truncate max-w-[150px] shrink-0 mr-2">
                      {currentAmbulance.documents.puc}
                    </span>
                  )}
                </div>
                <p className="text-[11px] text-slate-400 mt-1">
                  Preview only; files are not uploaded.
                </p>
              </div>

              {/* Ambulance Registration / Approval */}
              <div>
                <label className="text-xs font-semibold text-slate-800 block mb-1.5">
                  Ambulance Registration / Approval
                </label>
                <div className="border border-slate-200 rounded-lg p-1.5 bg-white flex items-center justify-between">
                  <input
                    type="file"
                    onChange={(e) => handleDocFileChange('ambulance_registration', e)}
                    className="w-full text-xs text-slate-500 file:mr-3 file:py-1 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200 cursor-pointer"
                  />
                  {currentAmbulance.documents?.ambulance_registration && (
                    <span className="text-[11px] text-blue-600 font-medium truncate max-w-[150px] shrink-0 mr-2">
                      {currentAmbulance.documents.ambulance_registration}
                    </span>
                  )}
                </div>
                <p className="text-[11px] text-slate-400 mt-1">
                  Preview only; files are not uploaded.
                </p>
              </div>
            </div>

            {/* Column 2 */}
            <div className="space-y-4">
              {/* Insurance */}
              <div>
                <label className="text-xs font-semibold text-slate-800 block mb-1.5">
                  Insurance
                </label>
                <div className="border border-slate-200 rounded-lg p-1.5 bg-white flex items-center justify-between">
                  <input
                    type="file"
                    onChange={(e) => handleDocFileChange('insurance', e)}
                    className="w-full text-xs text-slate-500 file:mr-3 file:py-1 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200 cursor-pointer"
                  />
                  {currentAmbulance.documents?.insurance && (
                    <span className="text-[11px] text-blue-600 font-medium truncate max-w-[150px] shrink-0 mr-2">
                      {currentAmbulance.documents.insurance}
                    </span>
                  )}
                </div>
                <p className="text-[11px] text-slate-400 mt-1">
                  Preview only; files are not uploaded.
                </p>
              </div>

              {/* Permit */}
              <div>
                <label className="text-xs font-semibold text-slate-800 block mb-1.5">
                  Permit
                </label>
                <div className="border border-slate-200 rounded-lg p-1.5 bg-white flex items-center justify-between">
                  <input
                    type="file"
                    onChange={(e) => handleDocFileChange('permit', e)}
                    className="w-full text-xs text-slate-500 file:mr-3 file:py-1 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200 cursor-pointer"
                  />
                  {currentAmbulance.documents?.permit && (
                    <span className="text-[11px] text-blue-600 font-medium truncate max-w-[150px] shrink-0 mr-2">
                      {currentAmbulance.documents.permit}
                    </span>
                  )}
                </div>
                <p className="text-[11px] text-slate-400 mt-1">
                  Preview only; files are not uploaded.
                </p>
              </div>

              {/* Driver Licence */}
              <div>
                <label className="text-xs font-semibold text-slate-800 block mb-1.5">
                  Driver Licence
                </label>
                <div className="border border-slate-200 rounded-lg p-1.5 bg-white flex items-center justify-between">
                  <input
                    type="file"
                    onChange={(e) => handleDocFileChange('driver_licence', e)}
                    className="w-full text-xs text-slate-500 file:mr-3 file:py-1 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200 cursor-pointer"
                  />
                  {currentAmbulance.documents?.driver_licence && (
                    <span className="text-[11px] text-blue-600 font-medium truncate max-w-[150px] shrink-0 mr-2">
                      {currentAmbulance.documents.driver_licence}
                    </span>
                  )}
                </div>
                <p className="text-[11px] text-slate-400 mt-1">
                  Preview only; files are not uploaded.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Add Another Ambulance Button */}
        <div className="mb-8">
          <button
            type="button"
            onClick={handleAddAmbulance}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-xs sm:text-sm font-semibold text-slate-700 shadow-2xs transition-colors cursor-pointer"
          >
            <Plus className="w-4 h-4 text-slate-600" />
            <span>+ Add another ambulance</span>
          </button>
        </div>

        {/* Bottom Actions Bar */}
        <div className="flex items-center justify-between pt-4 border-t border-slate-200">
          <button
            type="button"
            onClick={goBack}
            className="px-6 py-2.5 rounded-xl border border-slate-300 bg-white hover:bg-slate-50 text-xs sm:text-sm font-semibold text-slate-700 shadow-2xs transition-colors cursor-pointer"
          >
            Back
          </button>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={saveAndExit}
              className="px-5 py-2.5 rounded-xl text-xs sm:text-sm font-semibold text-slate-600 hover:text-slate-800 hover:bg-slate-100 transition-colors cursor-pointer"
            >
              Save & exit
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="px-6 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs sm:text-sm shadow-sm transition-colors cursor-pointer disabled:opacity-60 flex items-center gap-2"
            >
              {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              <span>Save & continue</span>
            </button>
          </div>
        </div>
      </form>

      {/* Google Maps Pin Picker Modal */}
      {mapModalOpen && (
        <MapPinPicker
          isOpen={mapModalOpen}
          onClose={() => {
            setMapModalOpen(false);
            setActiveStandIdForMap(null);
          }}
          onConfirm={handleGoogleMapConfirm}
          initialLocation={
            (() => {
              const activeStand = (currentAmbulance.pickup_stands || []).find((s) => s.id === activeStandIdForMap);
              return activeStand?.latitude && activeStand?.longitude
                ? { latitude: Number(activeStand.latitude), longitude: Number(activeStand.longitude) }
                : null;
            })()
          }
          title="Select Pickup Stand Location"
          pinColor="#2563EB"
        />
      )}
    </div>
  );
};

export default Step5AddAmbulances;
